// Citas virtuales del médico para la Sala (Sala CRM↔HCE, rebanada 8b): carga, refresco por el canal en vivo (con el respaldo de 30 s de `useEventosSala`)
// y ejecución de acciones con su estado esperado. Es el gemelo de `useSalaDelDia` para las teleconsultas, que tienen su propio backend.
//
// Una cuenta sin telemedicina (403: enfermería, administrador sin ficha de médico) o sin sesión (401) no es un error: la lista queda vacía y no se
// vuelve a consultar en esta sesión de la pestaña.
import { useCallback, useEffect, useRef, useState } from 'react';
import { crearUltimaPeticion } from './ultimaPeticion';
import { useEventosSala } from './useEventosSala';
import { ErrorSala } from './salaHce';
import { obtenerVirtualesDelDia, type CitaVirtual } from './telemedicinaHce';

export type EstadoVirtuales = {
  citas: CitaVirtual[];
  /** Todavía no terminó la primera carga. */
  cargando: boolean;
  /** La última carga falló (red, 5xx): la lista conserva lo último que se vio. Un 401/403 (sin sesión o sin telemedicina) NO es un error. */
  error: boolean;
  /** `ahora` del servidor en la última respuesta (ISO) y la lectura monotónica del momento en que llegó: base de `ahoraServidorMs`. */
  ahoraIso: string;
  recibidoEnMs: number;
  /** Reloj monotónico actual (se refresca cada 15 s, lo justo para que «No asistió» aparezca a su hora sin pedir nada al servidor). */
  ahoraMs: number;
  /** Citas con una acción en curso (doble clic: el botón queda deshabilitado). */
  ocupadas: ReadonlySet<string>;
  recargar: () => Promise<void>;
  /** Igual que `ejecutar` de la Sala: devuelve el resultado o `null` si falló (el error ya se avisó con `alFallar`; si la cita cambió, se vuelve a pedir). */
  ejecutar: <R>(citaId: string, accion: () => Promise<R>, alFallar: (e: ErrorSala) => void) => Promise<R | null>;
};

export const REFRESCO_RELOJ_MS = 15_000;

const reloj = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function useVirtualesDelDia(): EstadoVirtuales {
  const [citas, setCitas] = useState<CitaVirtual[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [ahoraIso, setAhoraIso] = useState('');
  const [recibidoEnMs, setRecibidoEnMs] = useState(0);
  const [ahoraMs, setAhoraMs] = useState(reloj);
  const [ocupadas, setOcupadas] = useState<ReadonlySet<string>>(new Set());
  const ultima = useRef(crearUltimaPeticion());
  const enCurso = useRef(new Set<string>());
  const sinTelemedicina = useRef(false);

  const cargar = useCallback(async () => {
    if (sinTelemedicina.current) return;
    const peticion = ultima.current.nueva();
    try {
      const datos = await obtenerVirtualesDelDia();
      if (!peticion.vigente()) return;
      setCitas(Array.isArray(datos.citas) ? datos.citas : []);
      setAhoraIso(datos.ahora);
      setRecibidoEnMs(reloj());
      setAhoraMs(reloj());
      setError(false);
    } catch (err) {
      if (err instanceof ErrorSala && (err.status === 401 || err.status === 403)) {
        sinTelemedicina.current = err.status === 403;
        if (peticion.vigente()) {
          setCitas([]);
          setError(false);
        }
      } else if (peticion.vigente()) {
        // Cualquier otro fallo (red, 5xx): se conserva lo último que se vio y se avisa; el respaldo de 30 s (o «Reintentar») vuelve a pedir.
        setError(true);
      }
    } finally {
      if (peticion.vigente()) setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEventosSala(() => void cargar(), { tipos: ['sala', 'calendario'] });

  useEffect(() => {
    const h = setInterval(() => setAhoraMs(reloj()), REFRESCO_RELOJ_MS);
    return () => clearInterval(h);
  }, []);

  const ejecutar: EstadoVirtuales['ejecutar'] = useCallback(
    async (citaId, accion, alFallar) => {
      if (enCurso.current.has(citaId)) return null;
      enCurso.current.add(citaId);
      setOcupadas(new Set(enCurso.current));
      try {
        const resultado = await accion();
        void cargar();
        return resultado;
      } catch (err) {
        const e = err instanceof ErrorSala ? err : new ErrorSala('No se pudo completar la acción.', 0, 'error');
        alFallar(e);
        // 409/404: la cita cambió o ya no es de este médico. 0 (tiempo agotado o sin conexión): la acción pudo aplicarse aunque la respuesta no llegó.
        if (e.status === 409 || e.status === 404 || e.status === 0) void cargar();
        return null;
      } finally {
        enCurso.current.delete(citaId);
        setOcupadas(new Set(enCurso.current));
      }
    },
    [cargar],
  );

  return { citas, cargando, error, ahoraIso, recibidoEnMs, ahoraMs, ocupadas, recargar: cargar, ejecutar };
}
